import { PageHero, CTABand, SectionHead } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { IMAGES } from "../lib/constants";
import { useTitle } from "../lib/useTitle";
import { Sprout, Sun, Crown } from "lucide-react";

const PRINCIPLES = [
  "Every woman bears God's image and deserves dignity, safety, and beauty.",
  "Healing is holistic — spirit, mind, body, table, and soil together.",
  "Community is not a program add-on; it is the method.",
  "Self-sufficiency is grown through skills, work, and wise support.",
  "We heal best when we heal together — participants, volunteers, and professionals side by side.",
  "Graduates become leaders; the harvest always blesses the next planting.",
];

export default function MissionVision() {
  useTitle("Mission & Vision | Bee Courageous @ Garden Grub");
  return (
    <>
      <PageHero
        eyebrow="Mission & Vision"
        lines={["Rooted in faith.", "Growing toward freedom."]}
        lede="Our mission and vision guide every meal shared, every seed planted, and every courageous step a woman takes toward restoration."
        image={IMAGES.lavender}
        imageAlt="Purple lavender fields in bloom, echoing the wings of the Bee Courageous logo"
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <article className="card-soft h-full !border-wing/20 !bg-wing-soft/40" data-testid="mission-card">
              <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-wing text-white">
                <Sun className="h-6 w-6" aria-hidden="true" />
              </span>
              <h2 className="font-serif text-2xl font-semibold text-wing sm:text-3xl">Our Mission</h2>
              <p className="mt-4 text-base leading-relaxed text-charcoal/75 sm:text-lg">
                To walk with women healing from trauma, loss, and instability — nurturing them through faith-based empowerment,
                holistic health coaching, nourishment through food, gardening for sustainability, vocational training, and a
                loving community — over a three-year journey from survival to self-sufficiency and leadership.
              </p>
            </article>
          </Reveal>
          <Reveal delay={0.12}>
            <article className="card-soft h-full !border-garden/20 !bg-garden-soft/60" data-testid="vision-card">
              <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-garden text-white">
                <Sprout className="h-6 w-6" aria-hidden="true" />
              </span>
              <h2 className="font-serif text-2xl font-semibold text-garden sm:text-3xl">Our Vision</h2>
              <p className="mt-4 text-base leading-relaxed text-charcoal/75 sm:text-lg">
                A flourishing community where every woman who has been broken by life's storms is restored — spiritually,
                emotionally, physically, and economically — and rises to nourish her family, lead her community, and mentor
                the women who come after her.
              </p>
            </article>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Guiding Principles" title="The trellis everything grows on" />
            <Reveal delay={0.15}>
              <p className="mt-4 text-base leading-relaxed text-charcoal/70 sm:text-lg">
                Like a trellis gives a vine something to climb, these principles give our program its shape — steady, biblical,
                and always pointing upward.
              </p>
            </Reveal>
          </div>
          <ul className="space-y-4 lg:col-span-7">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={i} delay={0.06 * i}>
                <li className="flex items-start gap-4 rounded-2xl border border-garden/10 bg-white p-5 shadow-sm" data-testid={`principle-${i}`}>
                  <Crown className="mt-0.5 h-5 w-5 shrink-0 text-pollen-dark" aria-hidden="true" />
                  <span className="text-sm leading-relaxed text-charcoal/80 sm:text-base">{p}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <CTABand
        title="Believe in this mission? Help it bloom."
        text="Your time, prayers, and generosity turn mission statements into changed lives."
        primaryLabel="Get Involved"
      />
    </>
  );
}
